import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../../components/LoadingScreen';
import supabase from '../../utils/supabaseClient';

// Device class display names
const classNames = {
  'class_1': 'Class 1',
  'class_2': 'Class 2',
  'class_3': 'Class 3'
};

// Device class descriptions
const classDescriptions = {
  'class_1': 'Class 1 devices present minimal potential for harm to the user and are often simpler in design than Class 2 or Class 3 devices.',
  'class_2': 'Class 2 devices are those for which general controls alone are insufficient to assure safety and effectiveness.',
  'class_3': 'Class 3 devices are those that support or sustain human life, are of substantial importance in preventing impairment of human health, or present a potential, unreasonable risk of illness or injury.'
};

const ClassInputPage = () => {
  const { deviceClass } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Determine product category from URL path or state
  const [productCategory, setProductCategory] = useState('Medical Device');
  
  useEffect(() => {
    // Extract product category from path if it exists
    const path = location.pathname;
    if (path.includes('diagnostics')) {
      setProductCategory('Diagnostics');
    } else if (path.includes('digital_health')) {
      setProductCategory('Digital Health');
    }
  }, [location.pathname]);
  
  // Get properly formatted class name
  const className = classNames[deviceClass.toLowerCase()] || deviceClass;
  const classDescription = classDescriptions[deviceClass.toLowerCase()] || '';

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Product name is required');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Get current date in ISO format
      const currentDate = new Date().toISOString();
      
      // Create the initial product with just the name and device class
      const { data, error: supabaseError } = await supabase
        .from('products')
        .insert([
          {
            name,
            device_class: deviceClass.toLowerCase(),
            created_at: currentDate,
            updated_at: currentDate,
            user_id: user.id,
            product_category: productCategory,
            product_description: '',
            development_stage: 'Not Applicable'  // Default value for medical devices
          }
        ])
        .select();
      
      if (supabaseError) throw supabaseError;
      
      // Navigate to the class-specific details input page
      navigate(`/products/${data[0].id}/details/device/${deviceClass.toLowerCase()}`);
      
    } catch (err) {
      console.error('Error creating product:', err);
      setError('Failed to create product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading..." />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/products/create" className="text-primary-600 hover:text-primary-700 flex items-center">
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Class Selection
        </Link>
      </div>
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Create {className} {productCategory}</h1>
        <p className="text-gray-600">{classDescription}</p>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>{error}</p>
        </div>
      )}
      
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b">
            <div className="mb-6">
              <label htmlFor="name" className="block text-gray-700 text-sm font-bold mb-2">
                Product Name<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter the product name"
                required
              />
            </div>
            
            <div className="bg-blue-50 p-4 rounded-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    Enter the product name to create your {productCategory.toLowerCase()}. You'll be able to add class-specific details in the next step.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-6 py-4 flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/products/create')}
              className="btn-secondary mr-4"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Product & Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClassInputPage; 